import { useState } from "react";
import {
  Button,
  Card,
  Empty,
  Flex,
  List,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import { CreditCardOutlined, PlusOutlined } from "@ant-design/icons";
import { useListPaymentMethodsQuery } from "api";
import { AddPaymentMethodModal } from "./AddPaymentMethodModal";

interface BotsBillingPaymentSectionProps {
  /** Whether a Stripe Customer is provisioned. Add card is disabled otherwise. */
  hasStripeCustomer: boolean;
}

const BRAND_LABEL: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "Amex",
  unionpay: "UnionPay",
  diners: "Diners",
  discover: "Discover",
  jcb: "JCB",
};

export const BotsBillingPaymentSection = ({
  hasStripeCustomer,
}: BotsBillingPaymentSectionProps) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const { data, isLoading, refetch } = useListPaymentMethodsQuery(undefined, {
    skip: !hasStripeCustomer,
  });

  const methods = data?.data ?? [];

  return (
    <Card
      title="Metode de plata"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddModalOpen(true)}
          disabled={!hasStripeCustomer}
        >
          Adauga card
        </Button>
      }
    >
      {!hasStripeCustomer && (
        <Typography.Text type="secondary">
          Salveaza mai intai datele de facturare pentru a inregistra clientul
          in Stripe, apoi poti adauga un card.
        </Typography.Text>
      )}

      {hasStripeCustomer && isLoading && <Skeleton active paragraph={{ rows: 2 }} />}

      {hasStripeCustomer && !isLoading && methods.length === 0 && (
        <Empty description="Niciun card salvat. Adauga unul pentru a putea adauga boti." />
      )}

      {hasStripeCustomer && methods.length > 0 && (
        <List
          dataSource={methods}
          rowKey={(pm) => pm.id}
          renderItem={(pm) => (
            <List.Item>
              <List.Item.Meta
                avatar={<CreditCardOutlined style={{ fontSize: 20 }} />}
                title={
                  <Space>
                    <span>
                      {pm.brand ? BRAND_LABEL[pm.brand] ?? pm.brand : "Card"}{" "}
                      •••• {pm.last4 ?? "????"}
                    </span>
                    {pm.is_default && <Tag color="green">Implicit</Tag>}
                  </Space>
                }
                description={
                  pm.exp_month && pm.exp_year
                    ? `Expira ${String(pm.exp_month).padStart(2, "0")}/${pm.exp_year}`
                    : "—"
                }
              />
            </List.Item>
          )}
        />
      )}

      <AddPaymentMethodModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSaved={() => refetch()}
      />
    </Card>
  );
};
